import React, { useEffect, useState } from 'react';
import { View, ScrollView, Pressable } from 'react-native';
import { useQuery } from '@apollo/client';
import styled from 'styled-components/native';
import AnnouncementIcon from '../Sales/Announcements/AnnouncementIcon';
import Modal from 'app/src/elements/Modal';

import DataState from 'app/src/components/DataState';
import { Text } from 'app/src/styles';
import { dollars, numberWithDelimiter } from 'app/src/utils/stringHelpers';
import * as colors from 'app/src/styles/colors';

import {
  BorderedContainer,
  Title,
  Subtitle,
  Field,
  Label,
  Value,
  Going,
  GoingText,
  goingColor,
  LeftContainer,
  RightContainer,
} from './elements';
import { BIDDING_ASSET } from './queries';

const photoUrl = photo => {
  if ('object' === typeof photo) {
    return photo.url;
  } else {
    return photo;
  }
};

const Details = styled(ScrollView)`
`;

const MainPhoto = styled(Pressable)`
  height: 260px;
  align-items: center;
  text-align: center;
`;

const Carousel = styled(View)`
  flex-direction: row;
  max-height: 80px;
  margin-top: 10px;
  gap: 10px;
`;

const Notes = styled(View)`
  margin-top: 10px;
  margin-bottom: 10px;
  padding-bottom: 10px;

`;

const ChoiceContainer = styled(View)`
  flex-direction: row
  gap: 10px;
`;

const ChoiceTab = styled(Pressable)`
  ${props => props.active ? `background-color: ${colors.elRed};` : ''}
  padding: 5px;
`;

const ChoiceLabel = styled(Text)`
  ${props => props.active ? 'color: white;' : ''}
  font-weight: 600;
`;

const CurrentBidContainer = styled(View)`
  flex-direction: row;
  padding-bottom: 10px;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  border-bottom-color: black;
  justify-content: space-around;
`;

const CurrentBid = styled(Text)`
  font-weight: 600;
  font-size: 20px;
`;

const SaleAssetDetail = ({ saleAsset, groupSaleAssets, clerk }) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [activeSaleAsset, setActiveSaleAsset] = useState(saleAsset);
  const [saleAssetAnnouncements, setSaleAssetAnnouncements] = useState(false);
  const [announcementAssetDetails, setAnnouncementAssetDetails] = useState(false);
  const highBid = saleAsset.highBid && parseInt(saleAsset.highBid);

  const { data } = useQuery(BIDDING_ASSET, {
    variables: { id: activeSaleAsset.asset.id }
  });

  useEffect(() => {
    if (saleAsset && saleAsset.id !== activeSaleAsset.id) {
      setActiveSaleAsset(saleAsset);
    }
  }, [saleAsset, setActiveSaleAsset]);

  if (!data) { return <DataState.Loading />; }

  const { asset } = data;

  const allPhotos = [...asset.photos, ...asset.soarrPhotoUrls];

  const goToNextPhoto = () => {
    if (clerk) { return; }

    if (photoIndex >= allPhotos.length - 1) {
      setPhotoIndex(0);
    } else {
      setPhotoIndex(photoIndex + 1);
    }
  };

  const photo = allPhotos[photoIndex];

  let attributes = asset.generalDetails?.unit?.attributes
  if (!attributes) {
    attributes = {
      'Engine Make': asset.engineMfr,
      'Engine Type': asset.engineType,
      'Engine HP': asset.horsepower,
      'Transmission': asset.transmission,
      'Axel': asset.axel,
      'Sleeper': asset.sleeper,
      'Suspension Type': asset.suspension,
      'Ratio': asset.ratio,
      'Wheels Rear': asset.wheelsRear,
      'Wheels Front': asset.wheelsFront,
      'Wheels Base': asset.wheelsBase,
    }
  }

  return (
    <>
      <BorderedContainer style={{maxHeight: '59vh', height: '59vh'}}>
        {goingColor(saleAsset) && (
          <Going style={{ backgroundColor: goingColor(saleAsset) }}>
            <GoingText>{saleAsset.biddingStatus}</GoingText>
          </Going>
        )}
        {!clerk && (
          <CurrentBidContainer>
            <CurrentBid>
              {!!highBid ? `Current Bid: ${dollars(highBid)}` : 'No Current Bid'}
            </CurrentBid>
          </CurrentBidContainer>
        )}
        {groupSaleAssets && (
          <ChoiceContainer>
            {groupSaleAssets.map(entry => (
              <ChoiceTab
                key={entry.id}
                onPress={() => setActiveSaleAsset(entry)}
                active={activeSaleAsset.id === entry.id}
              >
                <ChoiceLabel active={activeSaleAsset.id === entry.id}>
                  {entry.correctedLotNumber}
                </ChoiceLabel>
              </ChoiceTab>
            ))}
          </ChoiceContainer>
        )}
        <View style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
          <View style={{display: 'flex', flexDirection: 'row'}}>
            <Title>{activeSaleAsset.correctedLotNumber}</Title>
            <AnnouncementIcon
              saleAsset={activeSaleAsset}
              setSaleAssetAnnouncements={setSaleAssetAnnouncements}
              setAnnouncementAssetDetails={setAnnouncementAssetDetails}
            />
          </View>
          <Subtitle style={{alignSelf: 'flex-end', justifySelf: 'flex-end'}}>{asset.name}</Subtitle>
        </View>
        <View style={{display: 'flex', flexDirection: 'row'}}>
          <LeftContainer>
            <Subtitle style={{alignSelf: 'flex-start'}}>
              {asset.cityState?.toUpperCase()}
            </Subtitle>
          </LeftContainer>
          <RightContainer>
            <Subtitle style={{alignSelf: 'flex-end'}}>
              {asset.vin}
            </Subtitle>
            <Subtitle style={{alignSelf: 'flex-end'}}>
              ODOMETER {asset.miles ? numberWithDelimiter(asset.miles) : ''}
            </Subtitle>
          </RightContainer>
        </View>
        <Details>
          {photoUrl(photo) && (
            <MainPhoto onPress={goToNextPhoto}>
              <img
                src={photoUrl(photo)}
                alt={asset.name}
                style={{maxWidth: '100%', maxHeight: '100%'}}
              />
            </MainPhoto>
          )}

          {!clerk && (
            <ScrollView horizontal>
              <Carousel>
                {allPhotos.map((photo, index) => (
                  <Pressable key={index} onPress={() => setPhotoIndex(index)}>
                    <img
                      src={photoUrl(photo)}
                      alt={asset.name}
                      style={{flex: 1, maxWidth: 100, maxHeight: '100%'}}
                    />
                  </Pressable>
                ))}
              </Carousel>
            </ScrollView>
          )}
          <ScrollView>
            <Notes>
              <Text
                style={{fontSize: '14px'}}
              >
                {asset.notes?.trim()}
              </Text>
              {asset.hours && (
                <Text>HOURS {numberWithDelimiter(asset.hours)}</Text>
              )}
            </Notes>
          </ScrollView>
          {!clerk && (
            Object.keys(attributes).map(label => (
              <React.Fragment key={label}>
                {attributes[label] ? (
                  <Field>
                    <Label>{label}</Label>
                    <Value>{attributes[label]}</Value>
                  </Field>
                ) : null}
              </React.Fragment>
            ))
          )}
        </Details>
      </BorderedContainer>
      {saleAssetAnnouncements &&
        <Modal
          showModal={saleAssetAnnouncements}
          setShowModal={setSaleAssetAnnouncements}
          modalType={`${announcementAssetDetails} Announcements`}
        >
          <View>
            {
              saleAssetAnnouncements.map(announcement => {
                return <View style={{margin: '10px'}}>
                  {announcement.body}
                </View>
              })
            }
          </View>
        </Modal>
      }
    </>
  )
};

export default SaleAssetDetail;
